<script setup lang="ts">
import { reactive } from "vue";
import { captureException } from "@sentry/vue";
import Question from "@/components/fields/Question.vue";
import AutoComplete from "@/components/fields/AutoComplete.vue";
import { useConfigStore } from "@/stores/config";
import { lookupConditions } from "@/services/api";
import { useToast } from "@/services/toast";
import { Suggestion } from "@/entities/suggestion";
import { MedicalCondition, PrescribedConditionTypes } from "@/entities/conditions";
import { debounce } from "@/utils";

export type MedicalState = {
    conditionSuggestions: Suggestion[],
    searchTerm: string,
}

export interface Props {
    id: string,
    label: string,
    type?: string,
    disableAddNotFound?: boolean,
}

const props = defineProps<Props>();

const emit = defineEmits<{
        (on:"select:condition", value: MedicalCondition): void
    }>();

const state: MedicalState = reactive({
    conditionSuggestions: [] as Suggestion[],
    searchTerm: "",
});

const { toast } = useToast();

const config = useConfigStore();

const allConditionsQuery = async (q: string) => {
    const config = useConfigStore();
    const conditions = await lookupConditions({ apiURL: config.apiUrl }, q);
    return props.type ? conditions.filter(cond => cond.type === props.type) : conditions;
};

const updateSuggestions = debounce(async (condition: string) => {
    if (condition.length >= 2) {
        try {
            state.conditionSuggestions = await allConditionsQuery(condition);
        } catch (err) {
            captureException(err);
            toast({
                useUnsafeHTMLMessage: true,
                timeout: config.toastTimeout,
                title: "We could not retrieve medical information at this time",
                message: `Please try again or call Annuity Ready on <a href='tel:08003265479'>0800 326 5479</a> Monday - Friday,
                            between 9-5, excluding Bank Holidays.`,
            });
        }
    }
}, 300);

const applySuggestion = async (condition: Suggestion) => {
    emit("select:condition", condition as MedicalCondition);
};

</script>
<template>
    <Question
        :name="id"
        :label="label"
        input-class="col-12"
    >
        <AutoComplete
            :id="id"
            v-model="state.searchTerm"
            :suggestions="state.conditionSuggestions.slice(0, 10)"
            not-found-message="Condition not listed"
            :not-found-suggestion="!disableAddNotFound ? {
                id: 0,
                name: state.searchTerm,
                type: PrescribedConditionTypes.Other,
                code: null
            } : undefined"
            :clear-input-after-click="false"
            @update:suggestion="updateSuggestions"
            @click:suggestion="applySuggestion"
        />
    </Question>
</template>
